import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Loader2, Save, Zap } from 'lucide-react';
import { toast } from 'sonner';

const NONE = '__none__';

export function SettingsPanel({ storeId, templates, onChanged }) {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!storeId) return;
    api.get('/recovery/settings', { params: { store_id: storeId } }).then((r) => setS(r.data)).catch(() => {});
  }, [storeId]);

  if (!s) return <div className="py-12 text-center text-zinc-400"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>;

  const upd = (k, v) => setS({ ...s, [k]: v });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/recovery/settings', s, { params: { store_id: storeId } });
      setS(data);
      toast.success('Paramètres enregistrés');
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec de la sauvegarde');
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl space-y-5" data-testid="recovery-settings-panel">
      <Card className={`p-5 border-2 ${s.auto_enabled ? 'border-emerald-300 bg-emerald-50/40' : 'border-zinc-200'}`}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="font-semibold flex items-center gap-2"><Zap className="h-4 w-4 text-amber-500" /> Relances automatiques</div>
            <p className="text-sm text-zinc-500 mt-1">Envoie automatiquement la relance (template par défaut) X minutes après l'abandon, dans la limite du quota journalier.</p>
          </div>
          <Switch checked={s.auto_enabled} onCheckedChange={(v) => upd('auto_enabled', v)} data-testid="auto-enabled-switch" />
        </div>
      </Card>

      <Card className="p-5 space-y-4">
        <div>
          <Label className="text-xs">Délai avant relance (minutes)</Label>
          <Input type="number" min={0} value={s.delay_minutes} onChange={(e) => upd('delay_minutes', Number(e.target.value))} data-testid="delay-input" />
          <p className="text-xs text-zinc-400 mt-1">Temps d'attente après l'abandon avant l'envoi automatique.</p>
        </div>
        <div>
          <Label className="text-xs">Limite d'emails par jour</Label>
          <Input type="number" min={1} max={100} value={s.daily_cap} onChange={(e) => upd('daily_cap', Number(e.target.value))} data-testid="daily-cap-input" />
          <p className="text-xs text-zinc-400 mt-1">Plan gratuit Resend = 100 emails/jour maximum (compte entier).</p>
        </div>
        <div>
          <Label className="text-xs">Adresse de réponse (reply-to)</Label>
          <Input value={s.reply_to} onChange={(e) => upd('reply_to', e.target.value)} placeholder="commande@mailing-commande.store" data-testid="reply-to-input" />
        </div>
        <div>
          <Label className="text-xs">Template par défaut</Label>
          <Select value={s.default_template_id || NONE} onValueChange={(v) => upd('default_template_id', v === NONE ? '' : v)}>
            <SelectTrigger data-testid="default-template-select"><SelectValue placeholder="Texte intégré par défaut" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>Texte intégré (par défaut)</SelectItem>
              {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </Card>

      <Button onClick={save} disabled={saving} data-testid="save-settings-button">
        {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />} Enregistrer
      </Button>
    </div>
  );
}
